<template>
  <Teleport to="body">
    <Transition name="registration-dialog">
      <div
        v-if="isOpen"
        class="registration-dialog-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="registration-dialog-title"
        @click.self="closeDialog"
      >
        <div class="registration-dialog-panel">
          <button
            type="button"
            class="registration-dialog-close"
            :aria-label="closeButtonText"
            @click="closeDialog"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div class="registration-dialog-header">
            <h2 id="registration-dialog-title" class="registration-dialog-title">
              {{ t('index.contactForm.title') }}
            </h2>
            <p class="registration-dialog-subtitle">{{ t('index.contactForm.subtitle') }}</p>
          </div>
          <ContactForm @submit="handleSubmit" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { ContactFormData } from '@/composables/contact/contact-form.types'

const { t } = useI18n()
const { isOpen, close: closeDialog } = useRegistrationFormDialog()

const closeButtonText = computed(() => t('common.close'))

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeDialog()
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener('keydown', onKeydown)
  } else {
    document.removeEventListener('keydown', onKeydown)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})

const handleSubmit = (_data: ContactFormData) => {
  setTimeout(closeDialog, 400)
}
</script>

<style scoped>
.registration-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  overflow-y: auto;
}

.registration-dialog-panel {
  position: relative;
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 24px 24px 32px;
  margin: auto;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  font-family: var(--font-inter);
}

.registration-dialog-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-grey);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s, color 0.2s;
}

.registration-dialog-close:hover {
  background: var(--color-light-grey);
  color: var(--color-dark-blue);
}

.registration-dialog-header {
  margin-bottom: 24px;
  padding-right: 32px;
}

.registration-dialog-title {
  margin: 0 0 8px;
  font-family: var(--font-inter);
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--color-dark-blue);
}

.registration-dialog-subtitle {
  margin: 0;
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  line-height: 1.6;
  color: var(--color-grey);
}

.registration-dialog-enter-active,
.registration-dialog-leave-active {
  transition: opacity 0.2s ease;
}

.registration-dialog-enter-from,
.registration-dialog-leave-to {
  opacity: 0;
}
</style>
